import { formatSalesOrderNumber } from '@order-management/format';
import { TemporaryAccessDashboardTableRow } from '@order-management/types';
import { useMemo, useState } from 'react';
import { Element } from 'react-bulma-components';
import { formatExpiresAt } from '../../shared/utils';
import Button from '../../ui/button/button';
import Link from '../../ui/link/link';
import PaginatedTable, {
  CustomPaginatedTableProps,
} from '../../ui/table/paginated-table/paginated-table';
import { Column, RowInteraction } from '../../ui/table/types';
import styles from './admin-temporary-access-management-table.module.scss';

export type AdminTemporaryAccessManagementTableProps =
  CustomPaginatedTableProps<TemporaryAccessDashboardTableRow> & {
    handleRevokeTemporaryAccess?: (id: string) => Promise<void>;
    loading?: boolean;
  };

export function AdminTemporaryAccessManagementTable({
  form,
  dataSource,
  onChange,
  config,
  empty,
  handleRevokeTemporaryAccess,
  loading,
}: AdminTemporaryAccessManagementTableProps) {
  const [activeRowId, setActiveRowId] = useState<string>('');

  const rowInteraction: RowInteraction = {
    onRowEnter: function (rowKey: string): void {
      setActiveRowId(rowKey);
    },
    onRowLeave: function (): void {
      setActiveRowId('');
    },
  };

  const columns: Column<TemporaryAccessDashboardTableRow>[] = useMemo(
    () => [
      {
        key: 'so.number',
        title: 'Sales Order',
        className: 'is-width-15',
        render: (el) => (
          <Link to={`sales-orders/${el.salesOrder.id}`}>
            {formatSalesOrderNumber(el.salesOrder.number)}
          </Link>
        ),
        sortable: true,
      },
      {
        key: 'u.email',
        title: 'User',
        className: 'is-width-15',
        render: (el) => el.user.email,
        sortable: false,
      },
      {
        key: 'o.name',
        title: 'Opportunity name',
        className: 'is-width-20',
        render: (el) => el.salesOrder.opportunity?.name,
        sortable: true,
      },
      {
        key: 'a.name',
        title: 'Account',
        className: 'is-width-20',
        render: (el) => el.salesOrder.opportunity?.account?.name,
        sortable: true,
      },
      {
        key: 'aParent.name',
        title: 'Parent account',
        className: 'is-width-20',
        render: (el) => el.salesOrder.opportunity?.account?.parent?.name,
        sortable: true,
      },
      {
        key: 'expiresAt',
        title: 'Remaining days',
        className: 'is-width-10',
        render: (el) => (
          <Element display="flex" alignItems="center">
            <span>{formatExpiresAt(el.expiresAt)}</span>
            <Button
              ml={1}
              text
              disabled={loading}
              className={`has-text-danger-dark is-size-7 ${
                el.id === activeRowId ? '' : styles.invisible
              }`}
              onClick={() => handleRevokeTemporaryAccess?.(el.id)}
            >
              <span className="is-underlined">Revoke</span>
            </Button>
          </Element>
        ),
        sortable: false,
      },
    ],
    [activeRowId, handleRevokeTemporaryAccess, loading]
  );

  return (
    <PaginatedTable
      form={form}
      dataSource={dataSource}
      columns={columns}
      onChange={onChange}
      config={config}
      rowKey={'id'}
      empty={empty}
      rowInteraction={rowInteraction}
    />
  );
}

export default AdminTemporaryAccessManagementTable;
